import { useState, useRef, MouseEvent, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FieldValues,
  useController,
  Control,
  Controller,
  Path,
} from 'react-hook-form'
import { Grid, Stack, Box, Typography, Button, TextField } from '@mui/material'
import { EntityPhoto, EntitySoundRecords } from '@/components'
import { SoundRecordUpload } from './SoundRecordUpload'
import { PatchMedia } from '@/hooks'
import {
  ControlledFieldProps,
  ApiErrorData,
  MediaType,
  Photo,
  SoundRecord,
} from '@/types'
import {
  capitalizeFirstLetter,
  getFieldErrorMessage,
  hasFieldError,
} from '@/utils'

export type ControlledMediaUploadProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  isReadonly?: boolean
  eventId?: number
  label?: string
  apiErrorData?: ApiErrorData
  apiFieldName?: string
  isFieldStateErrorShown?: boolean
  errorMessagePrefix?: string
  disabled?: boolean
  accept?: string
  soundRecord?: boolean
} & ControlledFieldProps<T>

export const ControlledMediaUpload = <T extends FieldValues>({
  name,
  control,
  apiErrorData,
  label,
  isFieldStateErrorShown = true,
  apiFieldName,
  errorMessagePrefix = 'errors:validation',
  disabled = false,
  accept,
  soundRecord = false,
}: ControlledMediaUploadProps<T>): JSX.Element | null => {
  const { t } = useTranslation()

  const [showPrompt, setShowPrompt] = useState(false)

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController<T>({
    name,
    control,
  })

  const media = value as PatchMedia

  const defineFileType = (file: File) => {
    const fileType = file.type

    if (fileType.startsWith('image/')) {
      return 'image'
    } else if (fileType.startsWith('video/')) {
      return 'video'
    }
  }
  const onChooseMedia = (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement

    if (target.files) {
      const chosenMedia = Array.from(target.files).map((mi) => ({
        file: mi,
        link: URL.createObjectURL(mi),
        description: '',
        type: defineFileType(mi),
      }))

      onChange({
        ...value,
        chosenToUpload: [...value.chosenToUpload, ...chosenMedia],
      })
    }
  }

  const onChangeMediaDescription = (index: number, newDescription: string) => {
    if (media && media.chosenToUpload) {
      const updatedMedia = [...media.chosenToUpload]

      updatedMedia[index] = {
        ...updatedMedia[index],
        description: newDescription,
      }

      onChange({
        ...value,
        chosenToUpload: updatedMedia,
      })
    }
  }

  const onDeleteChosenMedia = (index: number) => {
    if (media && media.chosenToUpload) {
      media.chosenToUpload.splice(index, 1)
      onChange({
        ...value,
        chosenToUpload: [...media.chosenToUpload],
      })
    }
  }

  const onDeleteUploadedMedia = (id: number) => {
    if (media && media.chosenToUpload) {
      onChange({ ...value, stale: [...value.stale, id] })
    }
  }

  const handleTriggerClick = (e: MouseEvent<HTMLInputElement>) => {
    if (!soundRecord) {
      return
    }

    const hasSeenPrompt = localStorage.getItem('showedSoundRecordUploadPrompt')

    if (!hasSeenPrompt) {
      e.preventDefault()
      setShowPrompt(true)
    }
  }

  const handleShowPrompt = () => {
    setShowPrompt(false)
    localStorage.setItem('showedSoundRecordUploadPrompt', 'true')

    fileInputRef.current?.click()
  }

  return (
    <Stack>
      <Typography>{label}</Typography>

      {showPrompt && (
        <Stack
          spacing={2}
          sx={{
            borderRadius: 1,
            backgroundColor: 'grey.200',
            my: 4,
            p: 2,
          }}
        >
          <Typography>
            <b>{t('soundRecords:texts.uploadSoundRecordings')}</b>
          </Typography>
          <ul>
            <li>{t('soundRecords:texts.uploadSoundRecordingsLi1')}</li>
            <li>{t('soundRecords:texts.uploadSoundRecordingsLi2')}</li>
            <li>{t('soundRecords:texts.uploadSoundRecordingsLi3')}</li>
            <li>{t('soundRecords:texts.uploadSoundRecordingsLi4')}</li>
          </ul>
          <Box alignSelf="center">
            <Button onClick={handleShowPrompt}>
              {t('common:buttons.closePrompt')}
            </Button>
          </Box>
        </Stack>
      )}

      {!disabled && (
        <Box width="33%">
          <Controller
            name={name}
            control={control}
            render={({ fieldState: { error } }) => (
              <TextField
                id={name}
                inputRef={fileInputRef}
                onClick={handleTriggerClick}
                onChange={onChooseMedia}
                value=""
                type="file"
                error={
                  !!error || hasFieldError(apiFieldName ?? name, apiErrorData)
                }
                helperText={
                  isFieldStateErrorShown && error?.message
                    ? t(
                        errorMessagePrefix
                          ? `${errorMessagePrefix}.${error.message}`
                          : error.message
                      )
                    : capitalizeFirstLetter(
                        getFieldErrorMessage(apiFieldName ?? name, apiErrorData)
                      )
                }
                inputProps={{
                  multiple: true,
                  ...(accept && { accept }),
                }}
              />
            )}
          />
        </Box>
      )}

      {media?.mediaType === MediaType.photos ? (
        <Grid container={true} spacing={2} mt={4}>
          {media.chosenToUpload.map((mi, index) => (
            <EntityPhoto
              photo={mi}
              onDelete={() => onDeleteChosenMedia(index)}
              key={index}
            />
          ))}
          {media.uploadedToApi.map(
            (mi, index) =>
              !media.stale.includes(mi.id) && (
                <EntityPhoto
                  photo={mi as Photo}
                  onDelete={
                    !disabled ? () => onDeleteUploadedMedia(mi.id) : undefined
                  }
                  key={`${mi.id}_${index}`}
                />
              )
          )}
        </Grid>
      ) : (
        <>
          {!!media?.chosenToUpload.length && (
            <Stack mt={4} spacing={2}>
              <Typography>
                {t('soundRecords:titles.newFilesToUpload')}
              </Typography>
              {media.chosenToUpload.map((mi, index) => (
                <SoundRecordUpload
                  soundRecord={mi}
                  onChange={(data) => onChangeMediaDescription(index, data)}
                  onDelete={() => onDeleteChosenMedia(index)}
                  error={error?.message}
                  key={index}
                />
              ))}
            </Stack>
          )}
          {!!media?.uploadedToApi.length && (
            <Stack
              sx={{
                borderRadius: 1,
                backgroundColor: 'grey.200',
                mt: 4,
                p: 0.5,
              }}
            >
              <Typography m={1}>
                {t('soundRecords:titles.uploadedSoundRecords')}
              </Typography>

              <EntitySoundRecords
                soundRecords={
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  media.uploadedToApi.filter(
                    (mi: SoundRecord) => !media.stale.includes(mi.id)
                  ) as SoundRecord[]
                }
                onDelete={!disabled ? onDeleteUploadedMedia : undefined}
              />
            </Stack>
          )}
        </>
      )}
    </Stack>
  )
}
